import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import useMedia from "../../hooks/useMedia";

const CG = "'Cormorant Garamond', Georgia, serif";
const RL = "'Raleway', sans-serif";

if (!document.getElementById("gallery-fonts")) {
  const l = document.createElement("link");
  l.id = "gallery-fonts"; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Raleway:wght@400;500;600;700&display=swap";
  document.head.appendChild(l);
}

if (!document.getElementById("gallery-kf")) {
  const s = document.createElement("style");
  s.id = "gallery-kf";
  s.textContent = `
    @keyframes galleryFadeIn {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes galleryLbIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes galleryImgIn {
      from { opacity: 0; transform: scale(0.94); }
      to   { opacity: 1; transform: scale(1); }
    }
    .gallery-item {
      break-inside: avoid;
      margin-bottom: 14px;
      border-radius: 14px;
      overflow: hidden;
      cursor: pointer;
      position: relative;
      display: block;
      animation: galleryFadeIn 0.5s ease both;
      background: #f5e6d0;
    }
    .gallery-item img {
      width: 100%;
      height: auto;
      display: block;
      transition: transform 0.5s cubic-bezier(.22,1,.36,1);
    }
    .gallery-item:hover img {
      transform: scale(1.04);
    }
    .gallery-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(to top, rgba(60,20,5,0.72) 0%, transparent 55%);
      opacity: 0;
      transition: opacity 0.3s ease;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      padding: 16px;
    }
    .gallery-item:hover .gallery-overlay {
      opacity: 1;
    }
    .gallery-lightbox {
      position: fixed;
      inset: 0;
      z-index: 99999;
      background: rgba(12, 6, 2, 0.92);
      display: flex;
      align-items: center;
      justify-content: center;
      animation: galleryLbIn 0.22s ease;
      touch-action: none;
    }
    .gallery-lightbox-img {
      max-width: min(88vw, 1100px);
      max-height: 85vh;
      border-radius: 10px;
      box-shadow: 0 40px 120px rgba(0,0,0,0.7);
      animation: galleryImgIn 0.3s cubic-bezier(.22,1,.36,1);
      object-fit: contain;
    }
    .gallery-nav-btn {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 52px;
      height: 52px;
      border-radius: 50%;
      background: rgba(255,255,255,0.1);
      border: 1px solid rgba(255,255,255,0.2);
      color: #fff;
      font-size: 26px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.2s, transform 0.2s;
      backdrop-filter: blur(8px);
    }
    .gallery-nav-btn:hover {
      background: rgba(255,255,255,0.2);
      transform: translateY(-50%) scale(1.08);
    }
    .gallery-close-btn {
      position: absolute;
      top: 20px;
      right: 20px;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: rgba(255,255,255,0.12);
      border: 1px solid rgba(255,255,255,0.2);
      color: #fff;
      font-size: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.2s;
      backdrop-filter: blur(8px);
    }
    .gallery-close-btn:hover { background: rgba(255,255,255,0.25); }
    .gallery-category-btn {
      padding: 6px 18px;
      border-radius: 20px;
      border: 1.5px solid rgba(0,0,0,0.1);
      background: rgba(255,255,255,0.7);
      color: #78716c;
      font-family: ${RL};
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 1px;
      text-transform: uppercase;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    .gallery-category-btn:hover {
      border-color: #d97706;
      color: #92400e;
      background: rgba(254,243,199,0.8);
    }
    .gallery-category-btn.active {
      background: linear-gradient(135deg, #fef3c7, #fde68a);
      border-color: #d97706;
      color: #92400e;
      box-shadow: 0 2px 12px rgba(217,119,6,0.2);
    }
    @media (max-width: 640px) {
      .gallery-nav-btn { width: 38px; height: 38px; font-size: 20px; }
      .gallery-nav-btn.left { left: 8px; }
      .gallery-nav-btn.right { right: 8px; }
    }
  `;
  document.head.appendChild(s);
}

function Lightbox({ images, currentIndex, onClose, onNavigate }) {
  const img = images[currentIndex];

  useEffect(() => {
    const fn = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNavigate("next");
      if (e.key === "ArrowLeft") onNavigate("prev");
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose, onNavigate]);

  return createPortal(
    <div className="gallery-lightbox" onPointerUp={onClose}>
      <button className="gallery-close-btn" onPointerUp={(e) => { e.stopPropagation(); onClose(); }}>✕</button>

      <button className="gallery-nav-btn left" style={{ left: 20 }}
        onPointerUp={(e) => { e.stopPropagation(); onNavigate("prev"); }}>‹</button>

      <div onPointerUp={(e) => e.stopPropagation()} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
        <img src={img.image_url} alt={img.caption || ""} className="gallery-lightbox-img" key={currentIndex} />
        {img.caption && (
          <p style={{ fontFamily: CG, fontSize: 15, color: "rgba(255,255,255,0.7)", textAlign: "center", maxWidth: 600 }}>
            {img.caption}
          </p>
        )}
        <p style={{ fontFamily: RL, fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 2 }}>
          {currentIndex + 1} / {images.length}
        </p>
      </div>

      <button className="gallery-nav-btn right" style={{ right: 20 }}
        onPointerUp={(e) => { e.stopPropagation(); onNavigate("next"); }}>›</button>
    </div>,
    document.body
  );
}

export default function Gallery() {
  const media = useMedia();
  const allImages = media.filter((m) => m.section === "gallery");

  const categories = ["All", ...Array.from(new Set(allImages.map(m => m.category || "Other"))).sort()];
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const filtered = activeCategory === "All"
    ? allImages
    : allImages.filter(m => (m.category || "Other") === activeCategory);

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const navigate = useCallback((dir) => {
    setLightboxIndex(prev => {
      if (prev === null) return null;
      if (dir === "next") return (prev + 1) % filtered.length;
      return prev - 1 < 0 ? filtered.length - 1 : prev - 1;
    });
  }, [filtered.length]);

  return (
    <main style={{ minHeight: "100vh", background: "linear-gradient(160deg, #fefce8 0%, #fffbf0 40%, #fafaf9 100%)", paddingBottom: 80 }}>

      {/* Header */}
      <header style={{ maxWidth: 1200, margin: "0 auto", padding: "64px 24px 40px", textAlign: "center" }}>
        <h1 style={{ fontFamily: CG, fontSize: "clamp(36px,5.5vw,64px)", fontWeight: 700, color: "#7c2d12", margin: "0 0 16px", lineHeight: 1.05, letterSpacing: "-0.5px" }}>
          Parish Gallery
        </h1>
        <div style={{ width: 56, height: 2, background: "linear-gradient(to right,#92400e,#d97706,transparent)", borderRadius: 2, margin: "0 auto 20px" }} />
        <p style={{ fontFamily: CG, fontSize: 18, color: "#78716c", maxWidth: 500, margin: "0 auto", lineHeight: 1.75 }}>
          Moments of faith, fellowship, and celebration from our parish community.
        </p>
      </header>

      {/* Category filters */}
      {categories.length > 2 && (
        <div style={{ maxWidth: 1200, margin: "0 auto 32px", padding: "0 24px", display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
          {categories.map(cat => (
            <button key={cat} className={`gallery-category-btn${activeCategory === cat ? " active" : ""}`}
              onClick={() => setActiveCategory(cat)}>
              {cat}
              <span style={{ marginLeft: 6, opacity: 0.5, fontWeight: 400 }}>
                {cat === "All" ? allImages.length : allImages.filter(m => (m.category || "Other") === cat).length}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Masonry Grid */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0", fontFamily: CG, fontSize: 20, color: "#c4b5a0", fontStyle: "italic" }}>
            No images yet — check back soon
          </div>
        ) : (
          <div style={{
            columns: "3 300px",
            columnGap: 14,
          }}>
            {filtered.map((img, i) => (
              <div
                key={i}
                className="gallery-item"
                style={{ animationDelay: `${i * 0.04}s` }}
                onClick={() => openLightbox(i)}
              >
                <img src={img.image_url} alt={img.caption || ""} loading="lazy" />
                <div className="gallery-overlay">
                  {img.category && (
                    <span style={{ fontFamily: RL, fontSize: 9, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "#fde68a", marginBottom: 4 }}>
                      {img.category}
                    </span>
                  )}
                  {img.caption && (
                    <p style={{ fontFamily: CG, fontSize: 14, color: "rgba(255,255,255,0.9)", margin: 0, lineHeight: 1.4 }}>
                      {img.caption}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={filtered}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onNavigate={navigate}
        />
      )}
    </main>
  );
}